import apiClient from './axios';
import type { ApiEnvelope } from '../types';

export type PayoutStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface EarningsSummary {
  payoutBalance: number;
  lockedInEscrow: number;
  pendingPayouts: number;
  lifetimeReleased: number;
  releasedLast7Days: number;
  releasedLast30Days: number;
  hasPayoutBank: boolean;
  /** Payout fee config — used to preview fee + net on the withdraw form. */
  payoutFeePercent: number;
  payoutFeeCap: number;
  /** Minimum withdrawable amount (canonical, from the backend). */
  minPayout: number;
}

export interface Bank {
  bankName: string;
  bankCode: string;
  logoUrl: string | null;
}

export interface ResolvedAccount {
  accountNumber: string;
  bankCode: string;
  accountName: string;
  bankName: string;
}

export interface TimelineEntry {
  kind: 'release' | 'payout' | 'shipping_fee';
  id: string;
  at: string;
  amount: number;
  meta: Record<string, unknown>;
}

export interface TimelineResponse {
  items: TimelineEntry[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface VendorPayout {
  id: string;
  vendorId: string;
  amount: number;
  status: PayoutStatus;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  reference: string;
  providerReference: string | null;
  failureReason: string | null;
  processedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  createdAt: string;
}

export interface PayoutListResponse {
  items: VendorPayout[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const earningsApi = {
  getSummary: async (): Promise<EarningsSummary> => {
    const { data } = await apiClient.get<ApiEnvelope<EarningsSummary>>(
      '/vendor/earnings/summary',
    );
    return data.data;
  },

  getTimeline: async (page = 1, limit = 50): Promise<TimelineResponse> => {
    const { data } = await apiClient.get<ApiEnvelope<TimelineResponse>>(
      `/vendor/earnings/timeline?page=${page}&limit=${limit}`,
    );
    return data.data;
  },

  listPayouts: async (page = 1, limit = 50): Promise<PayoutListResponse> => {
    const { data } = await apiClient.get<ApiEnvelope<PayoutListResponse>>(
      `/vendor/payouts?page=${page}&limit=${limit}`,
    );
    return data.data;
  },

  requestPayout: async (amount: number): Promise<VendorPayout> => {
    const { data } = await apiClient.post<ApiEnvelope<VendorPayout>>(
      '/vendor/payouts',
      { amount },
    );
    return data.data;
  },
};

export interface UpdateProfilePayload {
  /** Required by the backend on every profile save — couriers use it as the
   *  pickup contact. */
  ownerPhone: string;
  name?: string;
  location?: string | null;
  pickupAddressLine?: string | null;
  pickupCity?: string | null;
  pickupState?: string | null;
  pickupPhone?: string | null;
  pickupLat?: number | null;
  pickupLng?: number | null;
  pickupGooglePlaceId?: string | null;
  tagline?: string | null;
  specialty?: string | null;
  avatarUrl?: string | null;
  featuredImageUrl?: string | null;
  freeDelivery?: boolean;
  freeDeliveryMinOrderAmount?: number;
  freeDeliveryMaxSubsidyAmount?: number;
}

export interface UpdatePayoutBankPayload {
  accountNumber: string;
  bankCode: string;
  accountName: string;
}

export interface VendorProfile {
  id: string;
  slug: string;
  name: string;
  ownerEmail: string | null;
  ownerPhone: string | null;
  location: string | null;
  pickupAddressLine: string | null;
  pickupCity: string | null;
  pickupState: string | null;
  pickupPhone: string | null;
  pickupLat: number | null;
  pickupLng: number | null;
  pickupGooglePlaceId: string | null;
  tagline: string | null;
  specialty: string | null;
  avatarUrl: string | null;
  featuredImageUrl: string | null;
  freeDelivery: boolean;
  freeDeliveryMinOrderAmount: number;
  freeDeliveryMaxSubsidyAmount: number;
  status: 'pending' | 'active' | 'suspended';
  payoutBalance: number;
  payoutAccountNumber: string | null;
  payoutBankCode: string | null;
  payoutAccountName: string | null;
  rating: number;
  reviewsCount: number;
  createdAt: string;
}

export const profileApi = {
  get: async (): Promise<VendorProfile> => {
    const { data } = await apiClient.get<ApiEnvelope<VendorProfile>>(
      '/vendor/me/profile',
    );
    return data.data;
  },

  update: async (payload: UpdateProfilePayload): Promise<VendorProfile> => {
    const { data } = await apiClient.patch<ApiEnvelope<VendorProfile>>(
      '/vendor/me/profile',
      payload,
    );
    return data.data;
  },

  /** Upload + persist the storefront avatar; returns the updated profile. */
  uploadAvatar: async (imageBase64: string): Promise<VendorProfile> => {
    const { data } = await apiClient.post<ApiEnvelope<VendorProfile>>(
      '/vendor/me/avatar',
      { imageBase64 },
    );
    return data.data;
  },

  /** Upload + persist the wide storefront featured image (shown on the shop's
   *  seller cards); returns the updated profile. */
  uploadFeaturedImage: async (imageBase64: string): Promise<VendorProfile> => {
    const { data } = await apiClient.post<ApiEnvelope<VendorProfile>>(
      '/vendor/me/featured-image',
      { imageBase64 },
    );
    return data.data;
  },

  updatePayoutBank: async (
    payload: UpdatePayoutBankPayload,
  ): Promise<VendorProfile> => {
    const { data } = await apiClient.patch<ApiEnvelope<VendorProfile>>(
      '/vendor/me/payout-bank',
      payload,
    );
    return data.data;
  },

  getBanks: async (): Promise<Bank[]> => {
    const { data } = await apiClient.get<ApiEnvelope<Bank[]>>(
      '/vendor/me/banks',
    );
    return data.data;
  },

  resolveAccount: async (
    accountNumber: string,
    bankCode: string,
  ): Promise<ResolvedAccount> => {
    const { data } = await apiClient.post<ApiEnvelope<ResolvedAccount>>(
      '/vendor/me/resolve-account',
      { accountNumber, bankCode },
    );
    return data.data;
  },
};
